import { FC, Fragment, memo, useCallback, useState } from 'react'
import { IconName } from '@/client/components/Icon/types'
import { useAuthUser, useGetCartItems, useRouterPush } from '@/client/hooks'
import { DEFAULT_CURRENCY, format, getEffectiveCurrency, ROUTER_PATH } from '@/client/utils'
import { useI18n } from '@/locales'
import * as S from './styles'
import { CardPackageProps } from './types'
import { Button } from '../Button'
import { Icon } from '../Icon'
import { List } from '../List/List'
import { ListItem } from '../List/ListItem'
import { Text } from '../Text'
import { Tooltip } from '../Tooltip'

// WordPress content can embed a price inside rich text using a literal
// "{{CURRENCY}}" placeholder, e.g. "5 {{CURRENCY}} discount". Matches a leading number (dot or comma
// decimal) followed by that placeholder, so the number can be pulled out and
// run through the same currency formatter as `price`.
const CURRENCY_PLACEHOLDER_REGEX = /(\d+(?:[.,]\d+)?)\s*\{\{CURRENCY\}\}/g

// Replaces every "<number> {{CURRENCY}}" occurrence in `html` with a properly
// formatted currency value. A match whose number can't be safely cast is left
// untouched rather than passed to format.currency, to avoid runtime exceptions.
const formatCurrencyPlaceholders = (html: string, currencyCode: string, locale?: string): string =>
  html.replace(CURRENCY_PLACEHOLDER_REGEX, (match, rawNumber) => {
    const numericValue = Number(rawNumber.replace(',', '.'))
    return Number.isNaN(numericValue) ? match : format.currency(numericValue, currencyCode, locale)
  })

export const CardPackage: FC<CardPackageProps> = memo(
  ({
    version = 'old',
    kind = 'dark',
    name,
    description,
    list,
    price,
    prefix = '',
    suffix = '',
    observation,
    link,
    buttonLabel,
    image,
    index = '',
    redirectionRoute,
    restrictOrderToDefaultCurrency = false,
    ...rest
  }) => {
    const { routerPush, locale, defaultLocale } = useRouterPush()
    const { isAuthenticated } = useAuthUser()
    const t = useI18n()
    const [activeCardIndex, setActiveCardIndex] = useState('')
    const { data: cartData } = useGetCartItems()
    const currentCurrency = getEffectiveCurrency(cartData)
    // This package's button can commit an order in whatever currency is
    // currently active, so gate it to the shop's default currency when asked to.
    const isOrderDisabled = restrictOrderToDefaultCurrency && currentCurrency !== DEFAULT_CURRENCY

    // `price` comes from WordPress as a string and isn't guaranteed to be numeric
    // (e.g. "50,00" instead of "50.00"), so only run it through the currency formatter
    // when it actually casts to a number; otherwise fall back to the raw value.
    const numericPrice = Number(price)
    const isFormattablePrice = price !== '' && !Number.isNaN(numericPrice)

    const handleMouseEnter = useCallback(
      (index) => {
        setActiveCardIndex(index == activeCardIndex ? '0' : index)
      },
      [index]
    )

    const handleAuthorization = useCallback(() => {
      if (isAuthenticated) return routerPush(link)
      routerPush(`${ROUTER_PATH.LOGIN[defaultLocale]}?redirect=${encodeURIComponent(redirectionRoute ?? '')}`)
    }, [link, isAuthenticated, routerPush, defaultLocale])

    return (
      <S.Card
        version={version}
        kind={kind}
        isActive={activeCardIndex == index}
        onMouseEnter={() => handleMouseEnter(index)}
        onMouseLeave={() => setActiveCardIndex('0')}
        {...rest}
      >
        <>
          <S.CardHeader kind={kind} version={version}>
            {name}
          </S.CardHeader>

          <S.CardInfos version={version}>
            {description && (
              <S.Description>
                <S.Description>
                  {image?.map(({ key, icon }) => (
                    <Fragment key={key}>
                      <Icon name={icon} size='s7' color='black' />
                    </Fragment>
                  ))}
                </S.Description>

                <Text
                  margin={version === 'new' ? '0 0 2rem 1.5rem' : '0 0 1.5rem'}
                  weight='bold'
                  lineHeight='1.2'
                  size='xs'
                >
                  <span dangerouslySetInnerHTML={{ __html: description }} />
                </Text>
              </S.Description>
            )}

            <List kind='gold'>
              {list.map(({ key, icon, text, isBold }) => (
                <Fragment key={key}>
                  <ListItem
                    key={text}
                    iconName={icon as IconName}
                    weight={isBold ? 'medium' : 'normal'}
                    fontSize='small'
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: formatCurrencyPlaceholders(text, currentCurrency, locale)
                      }}
                    />
                  </ListItem>
                </Fragment>
              ))}
            </List>
          </S.CardInfos>
        </>

        <S.CardFooter version={version}>
          <Text color='weak' margin={!!observation ? '0 0 0.5rem' : '0 0 2rem'}>
            {`${prefix} `}
            <Text as='span' size='h3' weight='semiBlack' margin='0' padding={version === 'new' ? '0 0 0 2rem' : '0'}>
              {isFormattablePrice ? format.currency(numericPrice, currentCurrency, locale) : price}
            </Text>
            {` ${suffix}`}
          </Text>

          {observation && version === 'old' && (
            <Text size='sm' align='center' margin='0 0 2rem' padding='0 1.5rem'>
              <span dangerouslySetInnerHTML={{ __html: observation }} />
            </Text>
          )}

          {observation && version === 'new' && (
            <Text margin='0 0 0.5rem' padding='0 0.5rem 0 2rem' lineHeight='1.2' color='black'>
              <span dangerouslySetInnerHTML={{ __html: observation }} className='newObervationText' />
            </Text>
          )}

          <S.ButtonWrapper version={version} kind={kind}>
            {version === 'old' &&
              (isOrderDisabled ? (
                <Tooltip
                  title={
                    t('PACKAGE_ORDER_CURRENCY_DISABLED', { currency: DEFAULT_CURRENCY }) +
                    ' ' +
                    t('CHANGE_CURRENCY_CALCULATOR')
                  }
                >
                  <Button isOutlined kind='dark' disabled>
                    {buttonLabel}
                  </Button>
                </Tooltip>
              ) : (
                <Button isOutlined kind='dark' onClick={handleAuthorization}>
                  {buttonLabel}
                </Button>
              ))}
            {version === 'new' &&
              (isOrderDisabled ? (
                <Tooltip
                  title={
                    t('PACKAGE_ORDER_CURRENCY_DISABLED', { currency: DEFAULT_CURRENCY }) +
                    ' ' +
                    t('CHANGE_CURRENCY_CALCULATOR')
                  }
                >
                  <Button disabled className='buttonNewVersion'>
                    <Text color='inverse' weight='semiBlack' size='lg' margin='0.1rem 0 0'>
                      {buttonLabel}
                    </Text>
                    <Icon name='arrow-right' size='s9' />
                  </Button>
                </Tooltip>
              ) : (
                <Button onClick={handleAuthorization} className='buttonNewVersion'>
                  <Text color='inverse' weight='semiBlack' size='lg' margin='0.1rem 0 0'>
                    {buttonLabel}
                  </Text>
                  <Icon name='arrow-right' size='s9' />
                </Button>
              ))}
          </S.ButtonWrapper>
        </S.CardFooter>
      </S.Card>
    )
  }
)

CardPackage.displayName = 'CardPackage'
